import { Component, computed, input, linkedSignal, model, output } from '@angular/core';

import { IsoDate, formatIsoDateBr, parseIsoDate, toIsoDate } from '../../utils/iso-date';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const WEEKDAY_INITIALS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

type CalendarDayState = 'selected' | 'today' | 'available' | 'disabled';

interface CalendarDay {
  date: IsoDate;
  day: number;
  state: CalendarDayState;
  isToday: boolean;
  ariaLabel: string;
  /** Classe extra vinda de `dayClass` (ex: 'dangerous'), ou null. */
  customClass: string | null;
}

/** Regra que decide uma classe CSS extra para cada dia (ou null para nenhuma). */
export type DayClassFn = (date: IsoDate) => string | null;

/** Mês como número contínuo (ano * 12 + mês), para comparar e navegar entre meses sem `Date`. */
function monthKeyOf(date: IsoDate): number {
  const { year, monthIndex } = parseIsoDate(date);
  return year * 12 + monthIndex;
}

/**
 * Calendário mensal de seleção de um único dia, limitado a [min, max].
 * Trabalha exclusivamente com strings `yyyy-mm-dd` (entrada e saída), evitando
 * qualquer conversão de fuso horário.
 *
 * `dayClass` permite ao pai destacar dias por uma regra própria, sem que o
 * calendário conheça essa regra. A classe deve ser escrita de forma literal
 * (ex: uma classe global em styles.scss), nunca montada por concatenação.
 * Ela é aplicada por último e prevalece sobre o fundo padrão do dia; a seleção
 * continua visível pelo anel rosa.
 */
@Component({
  selector: 'app-date-picker',
  imports: [],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
})
export class DatePicker {
  readonly min = input.required<IsoDate>();
  readonly max = input.required<IsoDate>();
  /** Dia destacado como "hoje". */
  readonly today = input<IsoDate | null>(null);
  readonly value = model<IsoDate | null>(null);
  readonly dayClass = input<DayClassFn | null>(null);
  /** Emitido a cada clique num dia habilitado, mesmo que ele já esteja selecionado. */
  readonly daySelect = output<IsoDate>();

  protected readonly weekdayInitials = WEEKDAY_INITIALS;

  /** Mês exibido; acompanha a data selecionada (ou a máxima, se não houver seleção). */
  private readonly visibleMonthKey = linkedSignal(() => monthKeyOf(this.value() ?? this.max()));

  protected readonly monthLabel = computed(() => {
    const key = this.visibleMonthKey();
    return `${MONTH_NAMES[key % 12]} ${Math.floor(key / 12)}`;
  });

  protected readonly canGoToPreviousMonth = computed(() => this.visibleMonthKey() > monthKeyOf(this.min()));
  protected readonly canGoToNextMonth = computed(() => this.visibleMonthKey() < monthKeyOf(this.max()));

  /** Células da grade do mês: `null` para os espaços antes do dia 1. */
  protected readonly cells = computed<(CalendarDay | null)[]>(() => {
    const key = this.visibleMonthKey();
    const year = Math.floor(key / 12);
    const monthIndex = key % 12;
    const leadingBlanks = new Date(Date.UTC(year, monthIndex, 1)).getUTCDay();
    const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

    const min = this.min();
    const max = this.max();
    const today = this.today();
    const selected = this.value();
    const dayClass = this.dayClass();

    const days = Array.from({ length: daysInMonth }, (_, i): CalendarDay => {
      const date = toIsoDate(year, monthIndex, i + 1);
      const isToday = date === today;
      // Strings ISO yyyy-mm-dd são comparáveis lexicograficamente.
      const state: CalendarDayState =
        date < min || date > max ? 'disabled' : date === selected ? 'selected' : isToday ? 'today' : 'available';
      return {
        date,
        day: i + 1,
        state,
        isToday,
        ariaLabel: isToday ? `${formatIsoDateBr(date)} (hoje)` : formatIsoDateBr(date),
        customClass: dayClass?.(date) ?? null,
      };
    });

    return [...Array<null>(leadingBlanks).fill(null), ...days];
  });

  protected previousMonth(): void {
    if (this.canGoToPreviousMonth()) {
      this.visibleMonthKey.update((key) => key - 1);
    }
  }

  protected nextMonth(): void {
    if (this.canGoToNextMonth()) {
      this.visibleMonthKey.update((key) => key + 1);
    }
  }

  protected select(day: CalendarDay): void {
    if (day.state !== 'disabled') {
      this.value.set(day.date);
      this.daySelect.emit(day.date);
    }
  }
}
